export interface KeywordCategory {
  id: string;
  name: string;
  nameEs: string;
  keywords: string[];
  longTailPatterns: string[];
  h2Templates: {
    en: string;
    es: string;
  };
  h3Templates: {
    en: string[];
    es: string[];
  };
  contentTemplates: {
    en: string;
    es: string;
  };
  relatedCategories: string[];
}

export const cityKeywordCategories: KeywordCategory[] = [
  {
    id: 'general',
    name: 'General Psychology Services',
    nameEs: 'Servicios Generales de Psicología',
    keywords: ['psychologist', 'therapist', 'psychiatrist', 'counselor', 'mental health professional'],
    longTailPatterns: [
      // Original patterns
      'psychologist in {city}',
      'therapist near me {city}',
      'psychiatrist in {city}',
      'mental health services {city}',
      'therapy {city}',
      'counseling {city}',
      // Intent keywords (FASE 3)
      'find psychologist {city}',
      'best therapist in {city}',
      'top rated psychologist {city}',
      'affordable therapy {city}',
      'licensed therapist {city}',
      // Location keywords (FASE 3)
      'therapist near me in {city}',
      'psychologist downtown {city}',
      'mental health clinic {city}',
      // Urgency keywords (FASE 3)
      'immediate therapy {city}',
      'same day psychologist {city}',
      'emergency mental health {city}',
      // Additional long-tail (FASE 3)
      'experienced therapist {city}',
      'certified psychologist {city}',
      'professional counseling {city}'
    ],
    h2Templates: {
      en: 'Find Your Psychologist in {city}',
      es: 'Encuentra tu Psicólogo en {city}'
    },
    h3Templates: {
      en: ['Licensed Therapists', 'Mental Health Professionals', 'Counseling Services', 'Immediate Support Available'],
      es: ['Terapeutas Certificados', 'Profesionales de Salud Mental', 'Servicios de Consejería', 'Apoyo Inmediato Disponible']
    },
    contentTemplates: {
      en: 'In {city}, we connect you with qualified mental health professionals specialized in various therapeutic approaches. Our network includes licensed psychologists, experienced therapists, and certified psychiatrists ready to support your mental wellness journey.',
      es: 'En {city}, te conectamos con profesionales de salud mental cualificados especializados en diversos enfoques terapéuticos. Nuestra red incluye psicólogos certificados, terapeutas experimentados y psiquiatras listos para apoyar tu bienestar mental.'
    },
    relatedCategories: ['anxiety-depression', 'online-therapy']
  },
  {
    id: 'anxiety-depression',
    name: 'Anxiety & Depression Treatment',
    nameEs: 'Tratamiento de Ansiedad y Depresión',
    keywords: ['anxiety', 'depression', 'stress', 'panic attacks', 'insomnia', 'phobias'],
    longTailPatterns: [
      // Original patterns
      'anxiety therapist in {city}',
      'depression treatment {city}',
      'stress management {city}',
      'panic attack therapy {city}',
      'psychologist for anxiety {city}',
      'depression counseling {city}',
      // Intent keywords (FASE 3)
      'best anxiety therapist {city}',
      'top depression specialist {city}',
      'find anxiety help {city}',
      'affordable depression therapy {city}',
      // Location keywords (FASE 3)
      'anxiety therapist near me {city}',
      'depression clinic {city}',
      'stress management center {city}',
      // Urgency keywords (FASE 3)
      'immediate anxiety help {city}',
      'emergency depression support {city}',
      'urgent mental health care {city}',
      // Additional long-tail (FASE 3)
      'chronic anxiety treatment {city}',
      'clinical depression therapy {city}',
      'panic disorder specialist {city}',
      'social anxiety therapist {city}'
    ],
    h2Templates: {
      en: 'Anxiety & Depression Treatment in {city}',
      es: 'Tratamiento de Ansiedad y Depresión en {city}'
    },
    h3Templates: {
      en: ['Anxiety Therapy', 'Depression Treatment', 'Stress Management', 'Panic Disorder Support'],
      es: ['Terapia para la Ansiedad', 'Tratamiento de Depresión', 'Manejo del Estrés', 'Apoyo para Trastorno de Pánico']
    },
    contentTemplates: {
      en: 'Specialized psychologists in {city} offering evidence-based treatment for anxiety disorders, depression, and stress-related conditions. Our therapists use proven methods including CBT, mindfulness, and relaxation techniques.',
      es: 'Psicólogos especializados en {city} ofreciendo tratamiento basado en evidencia para trastornos de ansiedad, depresión y condiciones relacionadas con el estrés. Nuestros terapeutas utilizan métodos probados incluyendo TCC, mindfulness y técnicas de relajación.'
    },
    relatedCategories: ['general', 'cbt-therapy', 'trauma-ptsd']
  },
  {
    id: 'couples-family',
    name: 'Couples & Family Therapy',
    nameEs: 'Terapia de Pareja y Familia',
    keywords: ['couples therapy', 'marriage counseling', 'family therapy', 'relationship issues', 'divorce counseling'],
    longTailPatterns: [
      // Original patterns
      'couples therapy {city}',
      'marriage counselor in {city}',
      'family therapist {city}',
      'relationship counseling {city}',
      'couples therapist near me {city}',
      'marriage therapy {city}',
      // Intent keywords (FASE 3)
      'best couples therapist {city}',
      'find marriage counselor {city}',
      'top family therapist {city}',
      'affordable couples therapy {city}',
      // Location keywords (FASE 3)
      'couples counseling near me {city}',
      'family therapy center {city}',
      'marriage counseling clinic {city}',
      // Urgency keywords (FASE 3)
      'immediate couples help {city}',
      'urgent marriage counseling {city}',
      'crisis family therapy {city}',
      // Additional long-tail (FASE 3)
      'premarital counseling {city}',
      'divorce mediation therapy {city}',
      'blended family counseling {city}',
      'relationship crisis intervention {city}',
      'save my marriage therapy {city}'
    ],
    h2Templates: {
      en: 'Couples & Family Therapy in {city}',
      es: 'Terapia de Pareja y Familia en {city}'
    },
    h3Templates: {
      en: ['Marriage Counseling', 'Family Therapy', 'Relationship Support', 'Divorce Mediation'],
      es: ['Consejería Matrimonial', 'Terapia Familiar', 'Apoyo Relacional', 'Mediación de Divorcio']
    },
    contentTemplates: {
      en: 'Expert couples and family therapists in {city} helping strengthen relationships, improve communication, and resolve conflicts. Our specialists work with couples, families, and individuals navigating relationship challenges.',
      es: 'Terapeutas expertos de pareja y familia en {city} ayudando a fortalecer relaciones, mejorar la comunicación y resolver conflictos. Nuestros especialistas trabajan con parejas, familias e individuos navegando desafíos relacionales.'
    },
    relatedCategories: ['general', 'sexual-therapy']
  },
  {
    id: 'child-adolescent',
    name: 'Child & Adolescent Psychology',
    nameEs: 'Psicología Infantil y Adolescente',
    keywords: ['child psychologist', 'adolescent therapist', 'teen counseling', 'child therapy', 'pediatric psychology'],
    longTailPatterns: [
      // Original patterns
      'child psychologist in {city}',
      'adolescent therapist {city}',
      'teen counseling {city}',
      'child therapy {city}',
      'psychologist for children {city}',
      'therapist for teenagers {city}',
      // Intent keywords (FASE 3)
      'best child psychologist {city}',
      'find teen therapist {city}',
      'top pediatric psychologist {city}',
      'affordable child therapy {city}',
      // Location keywords (FASE 3)
      'child therapist near me {city}',
      'teen counseling center {city}',
      'pediatric psychology clinic {city}',
      // Urgency keywords (FASE 3)
      'immediate child help {city}',
      'urgent teen counseling {city}',
      'crisis child therapy {city}',
      // Additional long-tail (FASE 3)
      'school counseling {city}',
      'behavioral therapy for kids {city}',
      'adolescent depression treatment {city}',
      'child anxiety specialist {city}',
      'teen behavioral issues {city}'
    ],
    h2Templates: {
      en: 'Child & Adolescent Psychology in {city}',
      es: 'Psicología Infantil y Adolescente en {city}'
    },
    h3Templates: {
      en: ['Child Therapy', 'Teen Counseling', 'Behavioral Issues', 'School Problems'],
      es: ['Terapia Infantil', 'Consejería para Adolescentes', 'Problemas de Conducta', 'Problemas Escolares']
    },
    contentTemplates: {
      en: 'Specialized child and adolescent psychologists in {city} providing age-appropriate therapy for behavioral issues, emotional challenges, and developmental concerns. Our therapists create safe, supportive environments for young clients.',
      es: 'Psicólogos especializados en niños y adolescentes en {city} proporcionando terapia apropiada para problemas de conducta, desafíos emocionales y preocupaciones del desarrollo. Nuestros terapeutas crean entornos seguros y de apoyo.'
    },
    relatedCategories: ['neuropsychology', 'family-therapy']
  },
  {
    id: 'neuropsychology',
    name: 'Neuropsychology Services',
    nameEs: 'Servicios de Neuropsicología',
    keywords: ['neuropsychologist', 'cognitive assessment', 'brain injury', 'ADHD', 'autism', 'dementia'],
    longTailPatterns: [
      // Original patterns
      'neuropsychologist in {city}',
      'cognitive assessment {city}',
      'ADHD specialist {city}',
      'autism evaluation {city}',
      'neuropsychological testing {city}',
      'brain injury therapy {city}',
      // Intent keywords (FASE 3)
      'best neuropsychologist {city}',
      'find ADHD specialist {city}',
      'top autism evaluator {city}',
      'certified neuropsychologist {city}',
      // Location keywords (FASE 3)
      'neuropsychology clinic near me {city}',
      'ADHD testing center {city}',
      'autism assessment clinic {city}',
      // Urgency keywords (FASE 3)
      'immediate ADHD evaluation {city}',
      'urgent cognitive testing {city}',
      'fast autism diagnosis {city}',
      // Additional long-tail (FASE 3)
      'adult ADHD testing {city}',
      'memory assessment {city}',
      'learning disability evaluation {city}',
      'concussion treatment {city}',
      'dementia evaluation {city}'
    ],
    h2Templates: {
      en: 'Neuropsychology Services in {city}',
      es: 'Servicios de Neuropsicología en {city}'
    },
    h3Templates: {
      en: ['Cognitive Assessment', 'ADHD Evaluation', 'Autism Spectrum', 'Brain Injury Recovery'],
      es: ['Evaluación Cognitiva', 'Evaluación TDAH', 'Espectro Autista', 'Recuperación de Lesiones Cerebrales']
    },
    contentTemplates: {
      en: 'Expert neuropsychologists in {city} offering comprehensive cognitive assessments and rehabilitation services. Specializing in ADHD, autism spectrum disorders, brain injuries, and cognitive decline.',
      es: 'Neuropsicólogos expertos en {city} ofreciendo evaluaciones cognitivas completas y servicios de rehabilitación. Especializados en TDAH, trastornos del espectro autista, lesiones cerebrales y deterioro cognitivo.'
    },
    relatedCategories: ['child-adolescent', 'elderly']
  },
  {
    id: 'addictions',
    name: 'Addiction & Recovery',
    nameEs: 'Adicciones y Recuperación',
    keywords: ['addiction counseling', 'substance abuse', 'alcoholism', 'gambling addiction', 'recovery support'],
    longTailPatterns: [
      // Original patterns
      'addiction therapist {city}',
      'substance abuse counseling {city}',
      'alcoholism treatment {city}',
      'gambling addiction therapy {city}',
      'addiction recovery {city}',
      'drug addiction counselor {city}',
      // Intent keywords (FASE 3)
      'best addiction specialist {city}',
      'find rehab counselor {city}',
      'top recovery therapist {city}',
      'affordable addiction treatment {city}',
      // Location keywords (FASE 3)
      'addiction counselor near me {city}',
      'recovery support center {city}',
      'rehab therapy clinic {city}',
      // Urgency keywords (FASE 3)
      'immediate addiction help {city}',
      'urgent rehab support {city}',
      'emergency recovery services {city}',
      // Additional long-tail (FASE 3)
      'alcohol detox therapy {city}',
      'opioid addiction treatment {city}',
      'cocaine recovery support {city}',
      'marijuana addiction counseling {city}',
      'relapse prevention therapy {city}',
      'dual diagnosis treatment {city}'
    ],
    h2Templates: {
      en: 'Addiction & Recovery Services in {city}',
      es: 'Servicios de Adicciones y Recuperación en {city}'
    },
    h3Templates: {
      en: ['Substance Abuse Treatment', 'Alcohol Recovery', 'Gambling Addiction', 'Behavioral Addictions'],
      es: ['Tratamiento de Abuso de Sustancias', 'Recuperación de Alcoholismo', 'Adicción al Juego', 'Adicciones Conductuales']
    },
    contentTemplates: {
      en: 'Compassionate addiction specialists in {city} providing evidence-based treatment for substance abuse, alcoholism, gambling, and behavioral addictions. Our therapists support your journey to lasting recovery.',
      es: 'Especialistas compasivos en adicciones en {city} proporcionando tratamiento basado en evidencia para abuso de sustancias, alcoholismo, juego y adicciones conductuales. Nuestros terapeutas apoyan tu camino hacia la recuperación.'
    },
    relatedCategories: ['general', 'group-therapy']
  },
  {
    id: 'work-related',
    name: 'Work & Career Psychology',
    nameEs: 'Psicología Laboral y de Carrera',
    keywords: ['work stress', 'burnout', 'career counseling', 'workplace anxiety', 'professional coaching'],
    longTailPatterns: [
      // Original patterns
      'work stress therapist {city}',
      'burnout counseling {city}',
      'career psychologist {city}',
      'workplace anxiety therapy {city}',
      'executive coaching {city}',
      'professional development counselor {city}',
      // Intent keywords (FASE 3)
      'best burnout specialist {city}',
      'find career counselor {city}',
      'top executive coach {city}',
      'affordable work therapy {city}',
      // Location keywords (FASE 3)
      'career counselor near me {city}',
      'burnout therapy center {city}',
      'workplace wellness clinic {city}',
      // Urgency keywords (FASE 3)
      'immediate burnout help {city}',
      'urgent career counseling {city}',
      'emergency work stress support {city}',
      // Additional long-tail (FASE 3)
      'job transition therapy {city}',
      'leadership development {city}',
      'work life balance counseling {city}',
      'occupational stress management {city}',
      'performance anxiety therapy {city}'
    ],
    h2Templates: {
      en: 'Work & Career Psychology in {city}',
      es: 'Psicología Laboral y de Carrera en {city}'
    },
    h3Templates: {
      en: ['Burnout Prevention', 'Career Counseling', 'Workplace Anxiety', 'Leadership Coaching'],
      es: ['Prevención de Burnout', 'Consejería de Carrera', 'Ansiedad Laboral', 'Coaching de Liderazgo']
    },
    contentTemplates: {
      en: 'Specialized psychologists in {city} helping professionals manage work stress, prevent burnout, and achieve career goals. Our services include individual therapy, executive coaching, and workplace wellness programs.',
      es: 'Psicólogos especializados en {city} ayudando a profesionales a manejar el estrés laboral, prevenir el burnout y alcanzar metas de carrera. Nuestros servicios incluyen terapia individual, coaching ejecutivo y programas de bienestar laboral.'
    },
    relatedCategories: ['anxiety-depression', 'coaching']
  },
  {
    id: 'elderly',
    name: 'Psychology for Elderly',
    nameEs: 'Psicología para Adultos Mayores',
    keywords: ['geriatric psychology', 'elderly counseling', 'dementia support', 'aging issues', 'retirement adjustment'],
    longTailPatterns: [
      // Original patterns
      'psychologist for elderly {city}',
      'geriatric therapist {city}',
      'dementia counseling {city}',
      'aging therapy {city}',
      'retirement counselor {city}',
      'elderly mental health {city}',
      // Intent keywords (FASE 3)
      'best geriatric psychologist {city}',
      'find elderly counselor {city}',
      'top senior therapist {city}',
      'affordable elderly care {city}',
      // Location keywords (FASE 3)
      'senior therapist near me {city}',
      'geriatric counseling center {city}',
      'elderly mental health clinic {city}',
      // Urgency keywords (FASE 3)
      'immediate elderly support {city}',
      'urgent senior counseling {city}',
      'emergency geriatric care {city}',
      // Additional long-tail (FASE 3)
      'alzheimer support therapy {city}',
      'senior depression treatment {city}',
      'retirement adjustment counseling {city}',
      'late life anxiety therapy {city}',
      'elder grief counseling {city}'
    ],
    h2Templates: {
      en: 'Psychology Services for Elderly in {city}',
      es: 'Servicios de Psicología para Adultos Mayores en {city}'
    },
    h3Templates: {
      en: ['Geriatric Counseling', 'Dementia Support', 'Aging Adjustments', 'Grief & Loss'],
      es: ['Consejería Geriátrica', 'Apoyo para Demencia', 'Ajustes del Envejecimiento', 'Duelo y Pérdida']
    },
    contentTemplates: {
      en: 'Compassionate psychologists in {city} specializing in elderly care, addressing age-related challenges, cognitive decline, grief, and life transitions. Our therapists provide dignified, respectful support for seniors.',
      es: 'Psicólogos compasivos en {city} especializados en cuidado de adultos mayores, abordando desafíos relacionados con la edad, deterioro cognitivo, duelo y transiciones vitales. Nuestros terapeutas proporcionan apoyo digno y respetuoso.'
    },
    relatedCategories: ['neuropsychology', 'grief']
  },
  {
    id: 'cbt-therapy',
    name: 'Cognitive Behavioral Therapy',
    nameEs: 'Terapia Cognitivo Conductual',
    keywords: ['CBT', 'cognitive therapy', 'behavioral therapy', 'cognitive behavioral', 'evidence-based therapy'],
    longTailPatterns: [
      // Original patterns
      'CBT therapist {city}',
      'cognitive behavioral therapy {city}',
      'cognitive therapist {city}',
      'behavioral psychologist {city}',
      'CBT counselor {city}',
      // Intent keywords (FASE 3)
      'best CBT therapist {city}',
      'find cognitive behavioral therapist {city}',
      'top CBT specialist {city}',
      'certified CBT practitioner {city}',
      // Location keywords (FASE 3)
      'CBT therapy near me {city}',
      'cognitive behavioral clinic {city}',
      'CBT treatment center {city}',
      // Urgency keywords (FASE 3)
      'immediate CBT sessions {city}',
      'urgent cognitive therapy {city}',
      'fast CBT treatment {city}',
      // Additional long-tail (FASE 3)
      'CBT for anxiety {city}',
      'CBT for depression {city}',
      'CBT for OCD {city}',
      'online CBT therapy {city}',
      'group CBT sessions {city}',
      'intensive CBT program {city}'
    ],
    h2Templates: {
      en: 'Cognitive Behavioral Therapy (CBT) in {city}',
      es: 'Terapia Cognitivo Conductual (TCC) en {city}'
    },
    h3Templates: {
      en: ['CBT for Anxiety', 'CBT for Depression', 'Behavioral Modification', 'Thought Pattern Work'],
      es: ['TCC para Ansiedad', 'TCC para Depresión', 'Modificación Conductual', 'Trabajo de Patrones de Pensamiento']
    },
    contentTemplates: {
      en: 'Certified CBT therapists in {city} using evidence-based cognitive behavioral techniques to treat anxiety, depression, and various mental health conditions. CBT is one of the most researched and effective therapeutic approaches.',
      es: 'Terapeutas TCC certificados en {city} utilizando técnicas cognitivo-conductuales basadas en evidencia para tratar ansiedad, depresión y diversas condiciones de salud mental. La TCC es uno de los enfoques terapéuticos más investigados y efectivos.'
    },
    relatedCategories: ['anxiety-depression', 'general']
  },
  {
    id: 'emdr-therapy',
    name: 'EMDR Therapy',
    nameEs: 'Terapia EMDR',
    keywords: ['EMDR', 'trauma therapy', 'eye movement therapy', 'PTSD treatment', 'trauma processing'],
    longTailPatterns: [
      // Original patterns
      'EMDR therapist {city}',
      'EMDR therapy {city}',
      'trauma therapist {city}',
      'PTSD treatment {city}',
      'eye movement therapy {city}',
      // Intent keywords (FASE 3)
      'best EMDR therapist {city}',
      'find certified EMDR practitioner {city}',
      'top trauma specialist {city}',
      'experienced EMDR therapist {city}',
      // Location keywords (FASE 3)
      'EMDR therapy near me {city}',
      'trauma therapy center {city}',
      'EMDR treatment clinic {city}',
      // Urgency keywords (FASE 3)
      'immediate EMDR sessions {city}',
      'urgent trauma therapy {city}',
      'emergency PTSD treatment {city}',
      // Additional long-tail (FASE 3)
      'EMDR for PTSD {city}',
      'EMDR for anxiety {city}',
      'childhood trauma EMDR {city}',
      'complex trauma EMDR {city}',
      'intensive EMDR therapy {city}',
      'online EMDR sessions {city}'
    ],
    h2Templates: {
      en: 'EMDR Therapy in {city}',
      es: 'Terapia EMDR en {city}'
    },
    h3Templates: {
      en: ['Trauma Processing', 'PTSD Treatment', 'Complex Trauma', 'Childhood Trauma'],
      es: ['Procesamiento de Trauma', 'Tratamiento de TEPT', 'Trauma Complejo', 'Trauma Infantil']
    },
    contentTemplates: {
      en: 'Certified EMDR therapists in {city} specializing in trauma processing and PTSD treatment. EMDR (Eye Movement Desensitization and Reprocessing) is a powerful therapy for healing traumatic memories and experiences.',
      es: 'Terapeutas EMDR certificados en {city} especializados en procesamiento de trauma y tratamiento de TEPT. EMDR (Desensibilización y Reprocesamiento por Movimientos Oculares) es una terapia poderosa para sanar recuerdos y experiencias traumáticas.'
    },
    relatedCategories: ['trauma-ptsd', 'anxiety-depression']
  },
  {
    id: 'humanistic-therapy',
    name: 'Humanistic Therapy',
    nameEs: 'Terapia Humanista',
    keywords: ['humanistic therapy', 'person-centered', 'gestalt therapy', 'existential therapy', 'holistic therapy'],
    longTailPatterns: [
      // Original patterns
      'humanistic therapist {city}',
      'person-centered therapy {city}',
      'gestalt therapist {city}',
      'existential counselor {city}',
      'holistic psychologist {city}',
      // Intent keywords (FASE 3)
      'best humanistic therapist {city}',
      'find person-centered counselor {city}',
      'top gestalt therapist {city}',
      'certified humanistic psychologist {city}',
      // Location keywords (FASE 3)
      'humanistic therapy near me {city}',
      'person-centered counseling center {city}',
      'holistic therapy clinic {city}',
      // Urgency keywords (FASE 3)
      'immediate humanistic support {city}',
      'urgent person-centered therapy {city}',
      'same day holistic counseling {city}',
      // Additional long-tail (FASE 3)
      'gestalt therapy for anxiety {city}',
      'existential therapy for depression {city}',
      'holistic mental health {city}',
      'client-centered counseling {city}',
      'rogerian therapy {city}',
      'mindfulness humanistic therapy {city}'
    ],
    h2Templates: {
      en: 'Humanistic & Person-Centered Therapy in {city}',
      es: 'Terapia Humanista y Centrada en la Persona en {city}'
    },
    h3Templates: {
      en: ['Person-Centered Approach', 'Gestalt Therapy', 'Existential Counseling', 'Holistic Healing'],
      es: ['Enfoque Centrado en la Persona', 'Terapia Gestalt', 'Consejería Existencial', 'Sanación Holística']
    },
    contentTemplates: {
      en: 'Humanistic therapists in {city} offering person-centered, gestalt, and existential approaches focused on self-actualization, personal growth, and authentic living. Our therapists honor your unique journey and innate potential.',
      es: 'Terapeutas humanistas en {city} ofreciendo enfoques centrados en la persona, gestalt y existenciales enfocados en autorrealización, crecimiento personal y vida auténtica. Nuestros terapeutas honran tu viaje único y potencial innato.'
    },
    relatedCategories: ['general', 'personal-development']
  },
  {
    id: 'lgbtiq',
    name: 'LGBTIQ+ Affirming Therapy',
    nameEs: 'Terapia Afirmativa LGBTIQ+',
    keywords: ['LGBTQ therapy', 'gay therapist', 'transgender counseling', 'queer affirming', 'gender identity'],
    longTailPatterns: [
      // Original patterns
      'LGBTQ therapist {city}',
      'gay affirming counselor {city}',
      'transgender therapy {city}',
      'queer psychologist {city}',
      'gender identity counseling {city}',
      'LGBTIQ+ therapist {city}',
      // Intent keywords (FASE 3)
      'best LGBTQ therapist {city}',
      'find affirming counselor {city}',
      'top queer psychologist {city}',
      'certified gender therapist {city}',
      // Location keywords (FASE 3)
      'LGBTQ therapy near me {city}',
      'gay affirming clinic {city}',
      'transgender counseling center {city}',
      // Urgency keywords (FASE 3)
      'immediate LGBTQ support {city}',
      'urgent gender counseling {city}',
      'crisis queer therapy {city}',
      // Additional long-tail (FASE 3)
      'coming out therapy {city}',
      'non-binary counseling {city}',
      'gay couples therapy {city}',
      'trans affirming therapist {city}',
      'LGBTQ family therapy {city}',
      'gender dysphoria treatment {city}'
    ],
    h2Templates: {
      en: 'LGBTIQ+ Affirming Therapy in {city}',
      es: 'Terapia Afirmativa LGBTIQ+ en {city}'
    },
    h3Templates: {
      en: ['Identity Support', 'Coming Out Counseling', 'Transgender Care', 'Relationship Support'],
      es: ['Apoyo de Identidad', 'Consejería para Salir del Clóset', 'Cuidado Transgénero', 'Apoyo Relacional']
    },
    contentTemplates: {
      en: 'LGBTIQ+ affirming therapists in {city} providing culturally competent, inclusive care for sexual orientation, gender identity, coming out, relationships, and community-specific mental health needs.',
      es: 'Terapeutas afirmativos LGBTIQ+ en {city} proporcionando cuidado culturalmente competente e inclusivo para orientación sexual, identidad de género, salir del clóset, relaciones y necesidades de salud mental específicas de la comunidad.'
    },
    relatedCategories: ['general', 'couples-family']
  },
  {
    id: 'trauma-ptsd',
    name: 'Trauma & PTSD Treatment',
    nameEs: 'Tratamiento de Trauma y TEPT',
    keywords: ['trauma therapy', 'PTSD treatment', 'complex trauma', 'childhood trauma', 'traumatic stress'],
    longTailPatterns: [
      // Original patterns
      'trauma therapist {city}',
      'PTSD treatment {city}',
      'trauma counseling {city}',
      'complex trauma therapy {city}',
      'childhood trauma specialist {city}',
      'traumatic stress counselor {city}',
      // Intent keywords (FASE 3)
      'best trauma therapist {city}',
      'find PTSD specialist {city}',
      'top trauma psychologist {city}',
      'certified trauma counselor {city}',
      // Location keywords (FASE 3)
      'trauma therapy near me {city}',
      'PTSD treatment center {city}',
      'trauma counseling clinic {city}',
      // Urgency keywords (FASE 3)
      'immediate trauma support {city}',
      'urgent PTSD treatment {city}',
      'emergency trauma therapy {city}',
      // Additional long-tail (FASE 3)
      'sexual trauma therapy {city}',
      'combat PTSD treatment {city}',
      'developmental trauma {city}',
      'trauma focused CBT {city}',
      'somatic trauma therapy {city}',
      'complex PTSD treatment {city}'
    ],
    h2Templates: {
      en: 'Trauma & PTSD Treatment in {city}',
      es: 'Tratamiento de Trauma y TEPT en {city}'
    },
    h3Templates: {
      en: ['PTSD Recovery', 'Complex Trauma', 'Childhood Trauma', 'Traumatic Events'],
      es: ['Recuperación de TEPT', 'Trauma Complejo', 'Trauma Infantil', 'Eventos Traumáticos']
    },
    contentTemplates: {
      en: 'Specialized trauma therapists in {city} offering evidence-based treatment for PTSD, complex trauma, childhood trauma, and traumatic stress using EMDR, somatic therapy, and trauma-focused CBT.',
      es: 'Terapeutas especializados en trauma en {city} ofreciendo tratamiento basado en evidencia para TEPT, trauma complejo, trauma infantil y estrés traumático usando EMDR, terapia somática y TCC enfocada en trauma.'
    },
    relatedCategories: ['emdr-therapy', 'anxiety-depression']
  },
  {
    id: 'sexual-therapy',
    name: 'Sexual Therapy',
    nameEs: 'Terapia Sexual',
    keywords: ['sex therapy', 'sexual dysfunction', 'intimacy counseling', 'sexual health', 'couples intimacy'],
    longTailPatterns: [
      // Original patterns
      'sex therapist {city}',
      'sexual therapy {city}',
      'intimacy counselor {city}',
      'sexual dysfunction treatment {city}',
      'couples intimacy therapy {city}',
      // Intent keywords (FASE 3)
      'best sex therapist {city}',
      'find intimacy counselor {city}',
      'top sexual health specialist {city}',
      'certified sex therapist {city}',
      // Location keywords (FASE 3)
      'sex therapy near me {city}',
      'intimacy counseling center {city}',
      'sexual health clinic {city}',
      // Urgency keywords (FASE 3)
      'immediate intimacy help {city}',
      'urgent sexual therapy {city}',
      'quick sex counseling {city}',
      // Additional long-tail (FASE 3)
      'erectile dysfunction therapy {city}',
      'low libido counseling {city}',
      'premature ejaculation treatment {city}',
      'vaginismus therapy {city}',
      'sexual pain counseling {city}',
      'desire discrepancy therapy {city}'
    ],
    h2Templates: {
      en: 'Sexual & Intimacy Therapy in {city}',
      es: 'Terapia Sexual y de Intimidad en {city}'
    },
    h3Templates: {
      en: ['Sexual Dysfunction', 'Intimacy Issues', 'Couples Intimacy', 'Sexual Health'],
      es: ['Disfunción Sexual', 'Problemas de Intimidad', 'Intimidad de Pareja', 'Salud Sexual']
    },
    contentTemplates: {
      en: 'Certified sex therapists in {city} providing professional, confidential support for sexual dysfunction, intimacy concerns, and sexual health. Our specialists help individuals and couples enhance sexual wellness and satisfaction.',
      es: 'Terapeutas sexuales certificados en {city} proporcionando apoyo profesional y confidencial para disfunción sexual, preocupaciones de intimidad y salud sexual. Nuestros especialistas ayudan a individuos y parejas a mejorar el bienestar y satisfacción sexual.'
    },
    relatedCategories: ['couples-family', 'general']
  },
  {
    id: 'grief',
    name: 'Grief & Loss Counseling',
    nameEs: 'Consejería de Duelo y Pérdida',
    keywords: ['grief counseling', 'loss therapy', 'bereavement support', 'mourning counselor', 'death and dying'],
    longTailPatterns: [
      // Original patterns
      'grief counselor {city}',
      'grief therapy {city}',
      'bereavement support {city}',
      'loss counseling {city}',
      'mourning therapist {city}',
      'grief psychologist {city}',
      // Intent keywords (FASE 3)
      'best grief counselor {city}',
      'find bereavement therapist {city}',
      'top grief specialist {city}',
      'compassionate grief counselor {city}',
      // Location keywords (FASE 3)
      'grief therapy near me {city}',
      'bereavement support center {city}',
      'loss counseling clinic {city}',
      // Urgency keywords (FASE 3)
      'immediate grief support {city}',
      'urgent bereavement help {city}',
      'crisis grief counseling {city}',
      // Additional long-tail (FASE 3)
      'complicated grief therapy {city}',
      'child grief counseling {city}',
      'pet loss therapy {city}',
      'sudden loss counseling {city}',
      'anticipatory grief support {city}',
      'traumatic loss therapy {city}'
    ],
    h2Templates: {
      en: 'Grief & Loss Counseling in {city}',
      es: 'Consejería de Duelo y Pérdida en {city}'
    },
    h3Templates: {
      en: ['Bereavement Support', 'Loss Processing', 'Complicated Grief', 'Memorial Support'],
      es: ['Apoyo en Duelo', 'Procesamiento de Pérdida', 'Duelo Complicado', 'Apoyo Memorial']
    },
    contentTemplates: {
      en: 'Compassionate grief counselors in {city} supporting individuals through loss, bereavement, and mourning. Our therapists provide a safe space to process grief, honor memories, and find healing after loss.',
      es: 'Consejeros compasivos de duelo en {city} apoyando a individuos a través de pérdida, duelo y luto. Nuestros terapeutas proporcionan un espacio seguro para procesar el duelo, honrar memorias y encontrar sanación después de la pérdida.'
    },
    relatedCategories: ['elderly', 'trauma-ptsd']
  },
  {
    id: 'online-therapy',
    name: 'Online Therapy Services',
    nameEs: 'Servicios de Terapia Online',
    keywords: ['online therapy', 'teletherapy', 'virtual counseling', 'remote therapy', 'video therapy'],
    longTailPatterns: [
      // Original patterns
      'online therapist {city}',
      'teletherapy {city}',
      'virtual therapy {city}',
      'online counseling {city}',
      'remote psychologist {city}',
      'video therapy sessions {city}',
      // Intent keywords (FASE 3)
      'best online therapist {city}',
      'find teletherapy provider {city}',
      'top virtual counselor {city}',
      'affordable online therapy {city}',
      // Location keywords (FASE 3)
      'online therapy from {city}',
      'teletherapy serving {city}',
      'virtual counseling {city} area',
      // Urgency keywords (FASE 3)
      'immediate online therapy {city}',
      'same day teletherapy {city}',
      'instant virtual counseling {city}',
      // Additional long-tail (FASE 3)
      'online therapy platform {city}',
      'secure video therapy {city}',
      'evening online sessions {city}',
      'weekend teletherapy {city}',
      'online CBT therapy {city}',
      'virtual EMDR sessions {city}'
    ],
    h2Templates: {
      en: 'Online Therapy Services in {city}',
      es: 'Servicios de Terapia Online en {city}'
    },
    h3Templates: {
      en: ['Video Sessions', 'Flexible Scheduling', 'Secure Platform', 'Licensed Therapists'],
      es: ['Sesiones por Video', 'Horarios Flexibles', 'Plataforma Segura', 'Terapeutas Certificados']
    },
    contentTemplates: {
      en: 'Convenient online therapy services with licensed therapists in {city}. Access professional mental health support from home through secure video sessions. Same quality care with added flexibility and privacy.',
      es: 'Servicios convenientes de terapia online con terapeutas certificados en {city}. Accede a apoyo profesional de salud mental desde casa a través de sesiones de video seguras. Misma calidad de atención con flexibilidad y privacidad adicionales.'
    },
    relatedCategories: ['general', 'anxiety-depression']
  },
  {
    id: 'group-therapy',
    name: 'Group Therapy',
    nameEs: 'Terapia Grupal',
    keywords: ['group therapy', 'support groups', 'group counseling', 'therapeutic groups', 'peer support'],
    longTailPatterns: [
      // Original patterns
      'group therapy {city}',
      'support groups {city}',
      'group counseling {city}',
      'therapy groups {city}',
      'group sessions {city}',
      // Intent keywords (FASE 3)
      'best group therapy {city}',
      'find support group {city}',
      'join therapy group {city}',
      'affordable group counseling {city}',
      // Location keywords (FASE 3)
      'group therapy near me {city}',
      'support group center {city}',
      'group counseling clinic {city}',
      // Urgency keywords (FASE 3)
      'immediate group support {city}',
      'start group therapy {city}',
      'join support group today {city}',
      // Additional long-tail (FASE 3)
      'anxiety support group {city}',
      'depression therapy group {city}',
      'grief support group {city}',
      'addiction recovery group {city}',
      'women therapy group {city}',
      'men support group {city}'
    ],
    h2Templates: {
      en: 'Group Therapy & Support Groups in {city}',
      es: 'Terapia Grupal y Grupos de Apoyo en {city}'
    },
    h3Templates: {
      en: ['Therapeutic Groups', 'Peer Support', 'Specialized Groups', 'Community Healing'],
      es: ['Grupos Terapéuticos', 'Apoyo entre Pares', 'Grupos Especializados', 'Sanación Comunitaria']
    },
    contentTemplates: {
      en: 'Facilitated group therapy and support groups in {city} offering shared healing experiences, peer support, and community connection. Groups available for anxiety, depression, addiction recovery, grief, and more.',
      es: 'Terapia grupal facilitada y grupos de apoyo en {city} ofreciendo experiencias de sanación compartida, apoyo entre pares y conexión comunitaria. Grupos disponibles para ansiedad, depresión, recuperación de adicciones, duelo y más.'
    },
    relatedCategories: ['addictions', 'grief']
  },
  {
    id: 'personal-development',
    name: 'Personal Development & Coaching',
    nameEs: 'Desarrollo Personal y Coaching',
    keywords: ['life coaching', 'personal development', 'self-improvement', 'motivation', 'goal setting'],
    longTailPatterns: [
      // Original patterns
      'life coach {city}',
      'personal development counselor {city}',
      'self-improvement therapy {city}',
      'motivation coach {city}',
      'personal growth therapist {city}',
      // Intent keywords (FASE 3)
      'best life coach {city}',
      'find personal development expert {city}',
      'top motivation coach {city}',
      'certified life coach {city}',
      // Location keywords (FASE 3)
      'life coaching near me {city}',
      'personal development center {city}',
      'coaching services {city}',
      // Urgency keywords (FASE 3)
      'immediate coaching support {city}',
      'start personal development {city}',
      'quick life coaching {city}',
      // Additional long-tail (FASE 3)
      'career life coach {city}',
      'relationship life coach {city}',
      'wellness coaching {city}',
      'confidence coaching {city}',
      'success coaching {city}',
      'transformation coaching {city}'
    ],
    h2Templates: {
      en: 'Personal Development & Life Coaching in {city}',
      es: 'Desarrollo Personal y Coaching de Vida en {city}'
    },
    h3Templates: {
      en: ['Life Coaching', 'Goal Achievement', 'Self-Improvement', 'Personal Growth'],
      es: ['Coaching de Vida', 'Logro de Metas', 'Automejora', 'Crecimiento Personal']
    },
    contentTemplates: {
      en: 'Personal development specialists and life coaches in {city} helping you achieve goals, build confidence, and create meaningful life changes. Focus on motivation, self-improvement, and personal transformation.',
      es: 'Especialistas en desarrollo personal y coaches de vida en {city} ayudándote a alcanzar metas, construir confianza y crear cambios de vida significativos. Enfoque en motivación, automejora y transformación personal.'
    },
    relatedCategories: ['work-related', 'humanistic-therapy']
  },
  {
    id: 'eating-disorders',
    name: 'Eating Disorders Treatment',
    nameEs: 'Tratamiento de Trastornos Alimentarios',
    keywords: ['eating disorders', 'anorexia', 'bulimia', 'binge eating', 'body image', 'disordered eating'],
    longTailPatterns: [
      // Original patterns
      'eating disorder therapist {city}',
      'anorexia treatment {city}',
      'bulimia counseling {city}',
      'binge eating therapy {city}',
      'body image therapist {city}',
      'eating disorder specialist {city}',
      // Intent keywords (FASE 3)
      'best eating disorder specialist {city}',
      'find anorexia therapist {city}',
      'top bulimia counselor {city}',
      'certified eating disorder therapist {city}',
      // Location keywords (FASE 3)
      'eating disorder therapy near me {city}',
      'anorexia treatment center {city}',
      'eating disorder clinic {city}',
      // Urgency keywords (FASE 3)
      'immediate eating disorder help {city}',
      'urgent anorexia treatment {city}',
      'emergency eating disorder support {city}',
      // Additional long-tail (FASE 3)
      'orthorexia treatment {city}',
      'compulsive eating therapy {city}',
      'body dysmorphia counseling {city}',
      'intuitive eating counselor {city}',
      'food addiction therapy {city}',
      'eating disorder recovery {city}'
    ],
    h2Templates: {
      en: 'Eating Disorders Treatment in {city}',
      es: 'Tratamiento de Trastornos Alimentarios en {city}'
    },
    h3Templates: {
      en: ['Anorexia Treatment', 'Bulimia Support', 'Binge Eating Therapy', 'Body Image Counseling'],
      es: ['Tratamiento de Anorexia', 'Apoyo para Bulimia', 'Terapia de Atracones', 'Consejería de Imagen Corporal']
    },
    contentTemplates: {
      en: 'Specialized eating disorder therapists in {city} providing comprehensive treatment for anorexia, bulimia, binge eating, and body image concerns. Our multidisciplinary approach addresses physical, emotional, and psychological aspects.',
      es: 'Terapeutas especializados en trastornos alimentarios en {city} proporcionando tratamiento integral para anorexia, bulimia, atracones y preocupaciones de imagen corporal. Nuestro enfoque multidisciplinario aborda aspectos físicos, emocionales y psicológicos.'
    },
    relatedCategories: ['anxiety-depression', 'adolescent']
  }
];

// Helper function to get related categories
export const getRelatedCategories = (categoryId: string): KeywordCategory[] => {
  const category = cityKeywordCategories.find(cat => cat.id === categoryId);
  if (!category) return [];
  
  return cityKeywordCategories.filter(cat => 
    category.relatedCategories.includes(cat.id)
  );
};

// Generate all keyword variations for a city
export const generateCityKeywords = (cityName: string): string[] => {
  const allKeywords: string[] = [];
  
  cityKeywordCategories.forEach(category => {
    category.longTailPatterns.forEach(pattern => {
      allKeywords.push(pattern.replace('{city}', cityName));
    });
  });
  
  return allKeywords;
};

// Get category by ID
export const getCategoryById = (id: string): KeywordCategory | undefined => {
  return cityKeywordCategories.find(cat => cat.id === id);
};
